import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Plus, Trash2, Edit2, Check, ArrowUp, ArrowDown, Save, Link2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminFooterLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Edit / Add Form State
  const [editingId, setCopiedId] = useState(null); // null = adding, string = editing ID
  const [label, setLabel] = useState("");
  const [to, setTo] = useState("");
  const [target, setTarget] = useState("self");
  const [position, setPosition] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/footer-links");
      setLinks(r.data || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setCopiedId(null);
    setLabel("");
    setTo("");
    setTarget("self");
    setPosition(links.length ? Math.max(...links.map((l) => l.position || 0)) + 1 : 1);
    setError("");
  };

  const startEdit = (link) => {
    setCopiedId(link.id);
    setLabel(link.label);
    setTo(link.to);
    setTarget(link.target || "self");
    setPosition(link.position || 0);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !to.trim()) {
      setError("Please fill in both Label and To/URL fields");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        label: label.trim(),
        to: to.trim(),
        target,
        position: Number(position) || 0,
      };
      if (editingId) {
        await api.put(`/admin/footer-links/${editingId}`, payload);
        setSuccess("Footer link updated successfully!");
      } else {
        await api.post("/admin/footer-links", payload);
        setSuccess("Footer link created successfully!");
      }
      resetForm();
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (linkId) => {
    if (!window.confirm("Are you sure you want to delete this footer link?")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/footer-links/${linkId}`);
      setSuccess("Footer link deleted successfully!");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const move = async (link, direction) => {
    setError("");
    try {
      const idx = links.findIndex((l) => l.id === link.id);
      if (idx === -1) return;
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= links.length) return;

      const sibling = links[targetIdx];
      const posSelf = link.position || 0;
      const posSibling = sibling.position || 0;

      // Swap positions
      await Promise.all([
        api.put(`/admin/footer-links/${link.id}`, { ...link, position: posSibling }),
        api.put(`/admin/footer-links/${sibling.id}`, { ...sibling, position: posSelf }),
      ]);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]">
      <Header />
      
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-body)] uppercase tracking-wider hover:text-[var(--brand-ink)] mb-1"
            >
              <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <h1 className="font-display text-2xl font-bold text-[var(--brand-ink)]">
              Footer Links Manager
            </h1>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Add, edit, or reorder the links displayed at the bottom of the public site.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <form onSubmit={handleSubmit} className="mb-8 rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            {editingId ? <Edit2 size={16} className="text-[#6CABDD]" /> : <Plus size={16} className="text-[#6CABDD]" />}
            {editingId ? "Edit Footer Link" : "Add New Footer Link"}
          </h2>

          {error ? (
            <p className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="mb-4 rounded-sm border-l-2 border-emerald-500 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 font-bold">
              {success}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Link Label (text shown)</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Legal &amp; Disclaimers"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Link To / URL</label>
              <input
                type="text"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="e.g. /legal or https://example.com"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Target Window</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              >
                <option value="self">Same tab (self)</option>
                <option value="blank">New tab (blank)</option>
              </select>
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Sort Position Index</label>
              <input
                type="number"
                value={position}
                onChange={(e) => setPosition(Number(e.target.value) || 0)}
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editingId ? "Save changes" : "Create Link"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="label-kicker text-[var(--brand-body)] underline underline-offset-4 hover:text-[var(--brand-ink)]"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        {/* Links list */}
        <div className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <Link2 size={16} className="text-[#6CABDD]" />
            Active Footer Links
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((id) => (
                <div key={id} className="h-16 animate-pulse rounded-md border border-black/10 bg-[#F8FAFC]" />
              ))}
            </div>
          ) : links.length === 0 ? (
            <p className="p-8 text-center text-sm text-[var(--brand-body)] italic">No footer links configured. Default fallbacks will load.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {links.map((link, idx) => (
                <li key={link.id} className="py-4 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="font-display font-bold text-sm text-[var(--brand-ink)]">
                      {link.label}
                    </p>
                    <p className="mt-0.5 truncate text-xs font-mono text-[var(--brand-body)]">
                      {link.to} {link.target === "blank" ? "(new tab)" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]">
                      <button
                        type="button"
                        onClick={() => move(link, "up")}
                        disabled={idx === 0}
                        className="p-2 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move left/up"
                      >
                        <ArrowUp size={12} />
                      </button>
                      <span className="h-4 w-px bg-black/10" />
                      <button
                        type="button"
                        onClick={() => move(link, "down")}
                        disabled={idx === links.length - 1}
                        className="p-2 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move right/down"
                      >
                        <ArrowDown size={12} />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => startEdit(link)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 px-3 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5"
                    >
                      <Edit2 size={10} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(link.id)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-[#E63946] px-3 text-xs font-semibold text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={10} /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
